import React, { useState } from "react";
import { Upload, CheckCircle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import "../styles/submitTool.css";

const CATEGORIES = ["LLM", "Video", "Image Generation", "Audio", "Developer Tools", "No-Code", "Design", "Content Creation", "Productivity", "Marketing", "Finance", "Legal", "Education", "Entertainment", "Human Resources", "E-mail", "Logo creation", "Websites & Design", "Life Assistants", "Memory", "Healthcare"];
const PRICING_MODELS = ["Free", "Freemium", "Paid"];

export default function SubmitTool() {
  const [formData, setFormData] = useState({
    name: "",
    website_url: "",
    category: "",
    pricing: "Free",
    description: "",
    icon: "",
    email: "",
    additional_info: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch("http://localhost:5005/api/ai-tools", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error("Failed to submit tool. Please try again.");

      setIsSuccess(true);
      setFormData({
        name: "",
        website_url: "",
        category: "",
        pricing: "Free",
        description: "",
        icon: "",
        email: "",
        additional_info: ""
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="submit-success-container">
        <div className="success-card">
          <CheckCircle className="success-icon" size={64} />
          <h2>Successfully Submitted!</h2>
          <p>Thank you for contributing! Your tool has been aggregated and added to our main directory list.</p>
          <div className="success-actions">
            <Link to="/" className="btn-primary">Back to Directory</Link>
            <button onClick={() => setIsSuccess(false)} className="btn-secondary">Submit Another</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="submit-page-wrapper">
      <div className="submit-header">
        <Link to="/" className="back-link"><ArrowLeft size={16} /> Back to Home</Link>
        <h1>Share Your <span>AI Tool</span> with the World</h1>
        <p>Join our growing community of AI innovators! Submit your tool to our directory and connect with thousands of users actively searching for AI solutions like yours.</p>
        <p className="highlight-text">⭐ Featured submissions get highlighted on our homepage!</p>
      </div>

      <div className="submit-content-layout">
        <form className="submit-form" onSubmit={handleSubmit}>
          <h3>What are you submitting?</h3>

          {error && <div className="form-error-msg">{error}</div>}

          <div className="form-group">
            <label>Tool Name *</label>
            <input 
              type="text" 
              name="name" 
              placeholder="e.g., ChatGPT" 
              value={formData.name} 
              onChange={handleChange} 
              required 
            />
          </div>

          <div className="form-group">
            <label>Website URL *</label>
            <input 
              type="url" 
              name="website_url" 
              placeholder="https://example.com" 
              value={formData.website_url} 
              onChange={handleChange} 
              required 
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category *</label>
              <select name="category" value={formData.category} onChange={handleChange} required>
                <option value="">Select a category</option>
                {CATEGORIES.map((cat, idx) => (
                  <option key={idx} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Pricing Model *</label>
              <select name="pricing" value={formData.pricing} onChange={handleChange} required>
                {PRICING_MODELS.map((price, idx) => (
                  <option key={idx} value={price}>{price}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Short Description * ({formData.description.length}/500 chars)</label>
            <textarea 
              name="description" 
              maxLength={500} 
              placeholder="Provide a brief, compelling description of your AI tool (what it does, key features, who it's for)..." 
              value={formData.description} 
              onChange={handleChange} 
              required 
            />
          </div>

          <div className="form-group">
            <label>Logo URL (Optional)</label>
            <input 
              type="url" 
              name="icon" 
              placeholder="https://example.com/logo.png" 
              value={formData.icon} 
              onChange={handleChange} 
            />
          </div>

          <div className="form-group">
            <label>Your Email *</label>
            <input 
              type="email" 
              name="email" 
              placeholder="your@email.com" 
              value={formData.email} 
              onChange={handleChange} 
              required 
            />
          </div>

          <div className="form-group">
            <label>Additional Information (Optional)</label>
            <textarea 
              name="additional_info" 
              placeholder="Any other details you'd like to share about your tool..." 
              value={formData.additional_info} 
              onChange={handleChange} 
            />
          </div>

          <button type="submit" className="submit-submit-btn" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : "🚀 Submit Your Tool"}
          </button>
        </form>

        <div className="submit-perks-sidebar">
          <h3>Why Submit Your Tool?</h3>
          
          <div className="perk-item">
            <div className="perk-icon-wrap">👥</div>
            <div className="perk-text">
              <h4>Reach Thousands of Users</h4>
              <p>Get your tool in front of an engaged audience actively searching for AI solutions.</p>
            </div>
          </div>

          <div className="perk-item">
            <div className="perk-icon-wrap">⭐</div>
            <div className="perk-text">
              <h4>Featured Opportunities</h4>
              <p>Outstanding tools get featured on our homepage, driving even more visibility and traffic.</p>
            </div>
          </div>

          <div className="perk-item">
            <div className="perk-icon-wrap">📈</div>
            <div className="perk-text">
              <h4>Boost Your Visibility</h4>
              <p>Increase brand awareness and attract potential users, customers, and investors.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
